import React, {
    ReactElement, useEffect, useMemo, useRef, useState,
} from "react";
import { Toast } from "react-bootstrap";

import Styles from "./TimedToast.module.scss";

interface Props {
    title: string;
    children: ReactElement;
    seconds?: number;
    canCancel?: boolean;
    onCancel?: () => void;
    onClose?: () => void;
}

export default function TimedToast({
    title, children, seconds, onClose, canCancel, onCancel,
}: Props): ReactElement | null {
    const [timeleft, setTimeLeft] = useState(seconds ?? 5);
    const completedReference = useMemo<{
        isFinished: boolean;
        isCanceled: boolean;
    }>(() => ({
        isFinished: false,
        isCanceled: false,
    }), []);

    useEffect(() => {
        if (seconds) {
            setTimeLeft(seconds);
        }
    }, [seconds]);

    const handleClose = () => {
        setTimeLeft(0);
        completedReference.isFinished = true;
        onClose?.();
    };

    useEffect(() => {
        if (timeleft === 0) {
            completedReference.isFinished = true;
            onClose?.();
        }
        setTimeout(() => {
            setTimeLeft(current => Math.max(current - 1, 0));
        }, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeleft]);

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            if (!completedReference.isFinished && !completedReference.isCanceled) {
                handleClose();
            }
        };
    }, []);

    if (timeleft <= 0) {
        return null;
    }

    const handleCancel = () => {
        setTimeLeft(0);
        completedReference.isCanceled = true;
        completedReference.isFinished = true;
        onCancel?.();
    };

    return (

        <Toast onClose={handleClose} className={Styles.toast}>
            <Toast.Header>
                <strong className="me-auto">
                    {title}
                </strong>
                <small>
                    Closing in
                    {" "}
                    {timeleft}
                </small>
            </Toast.Header>
            <Toast.Body>
                <div className="d-flex justify-content-between p-2">
                    {children}
                    {canCancel && (
                        <button
                            onClick={handleCancel}
                            type="button"
                            className="btn btn-none btn-sm"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </Toast.Body>
        </Toast>

    );
}

TimedToast.defaultProps = {
    seconds: 5,
    onClose: undefined,
    canCancel: false,
    onCancel: undefined,
};
